import React, { FC, useLayoutEffect, useState, useRef, useEffect, useMemo } from 'react';
import qs from 'qs';
import { throttle } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import updeep from 'updeep';
import { Wrapper } from '@src/components/Wrapper';
import {
  useGetTalentActivityFeedQuery,
  GetTalentActivityFeedQuery,
  useGetTalentActivityStatisticsQuery,
  TalentActivityFeedSignalEnum,
  SignalTypeEnum,
  SignalEntityTypeEnum,
  GetVerifiedUserDocument,
} from '@src/graphql/generated';
import Checkbox from '@src/components/Checkbox';
import { FUNNEL_ROUTE } from '@src/Routes/links';
import { recordSignals } from '@src/graphql/signals/actions';
import { createLink } from '@src/util/createLink';
import { trackEvent } from '@src/metrics';
import apolloClient from '@src/graphql/apollo';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { setModalContent } from '@src/redux/actions/modal';
import { InviteToInterview } from '@src/components/VerifyTalentCard/InviteToInterview';
import { useAppDispatch } from '@src/redux';
import { QueryParams } from './Filters/interfaces/queryParams.interface';
import { Statistics } from './Statistics';
import { Banner } from './Banner';
import { Filters } from './Filters';
import { TalentsFeed, MODAL_OPTIONS } from './TalentsFeed';
import './styles.scss';

export const TalentActivity: FC = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();

  const history = useHistory();

  const checkBoxWrapperRef = useRef<HTMLDivElement>(null);

  const headerContentRef = useRef<HTMLDivElement>(null);

  const [shouldIncludeNonVerifiedUsers, setShouldIncludeNonVerifiedUsers] = useState(true);

  const queryParams: QueryParams = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }) || {},
    [location.search],
  );
  const selectedPositionIds = queryParams.positions || [];
  const viewVerifiedUser = queryParams?.viewVerifiedUser;
  const selectedSignal: TalentActivityFeedSignalEnum[] =
    queryParams.signal === 'ALL' || !queryParams.signal
      ? [TalentActivityFeedSignalEnum.VIEW_DETAILS, TalentActivityFeedSignalEnum.START_APPLY]
      : [queryParams.signal];

  const {
    data,
    loading: isLoading,
    fetchMore,
    updateQuery,
  } = useGetTalentActivityFeedQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      filterInput: {
        includeNonVerifiedUsers: shouldIncludeNonVerifiedUsers,
        openingIds: selectedPositionIds,
        signals: selectedSignal,
      },
      paginationOptions: {
        limit: 10,
      },
    },
  });

  const { data: statistics, loading: isStatisticsLoading } = useGetTalentActivityStatisticsQuery({
    fetchPolicy: 'network-only',
    variables: {
      filterInput: {
        includeNonVerifiedUsers: shouldIncludeNonVerifiedUsers,
        openingIds: selectedPositionIds,
        signals: selectedSignal,
      },
    },
  });

  const updateRecruiterState = (userId: string): void => {
    updateQuery(
      updeep.updateIn(
        'talentActivityFeed.result',
        (result: GetTalentActivityFeedQuery['talentActivityFeed']['result']) =>
          result.map(users => {
            if (users.talent.user.id === userId) {
              return {
                ...users,
                talent: {
                  ...users.talent,
                  recruiterState: {
                    ...users.talent.recruiterState,
                    hasMessaged: true,
                  },
                },
              };
            }

            return users;
          }),
      ),
    );
  };

  useEffect(() => {
    trackEvent({
      context: MetricContext.TALENT_ACTIVITY_PAGE,
      interaction: MetricAction.TALENT_ACTIVITY_VIEW,
    });

    if (viewVerifiedUser) {
      // eslint-disable-next-line no-inner-declarations
      async function fetchVerifiedUser() {
        const {
          data: { verifiedUser },
        } = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: GetVerifiedUserDocument,
          variables: {
            userId: viewVerifiedUser,
            viewedByCompaniesPaginationOptions: {
              limit: 3,
            },
          },
        });

        // eslint-disable-next-line no-inner-declarations
        function startInvite() {
          dispatch(
            setModalContent({
              className: 'vt-invite-modal-wrapper',
              config: {
                withoutPadding: true,
              },
              content: (
                <InviteToInterview
                  modalOptions={MODAL_OPTIONS}
                  onMessaged={userId => updateRecruiterState(userId)}
                  userId={verifiedUser.user.id}
                />
              ),
            }),
          );
        }

        startInvite();
      }

      fetchVerifiedUser();
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [queryParams, shouldIncludeNonVerifiedUsers]);

  const applicantsCount = statistics?.talentActivityStatistics.applications;
  const offerViews = statistics?.talentActivityStatistics.offerViews;
  const result = data?.talentActivityFeed.result;
  const pagination = data?.talentActivityFeed.pagination;

  const loadMore = throttle(() => {
    const {
      body: { scrollHeight },
    } = document;
    const { innerHeight, scrollY } = window;
    const distanceFromBottom = scrollHeight - innerHeight - scrollY;

    const filtersDistanceFromTop = checkBoxWrapperRef?.current?.getBoundingClientRect().top || 0;

    if (
      filtersDistanceFromTop < 0 &&
      !headerContentRef.current?.classList.contains('talent-activity__header-content--active')
    ) {
      headerContentRef.current?.classList.add('talent-activity__header-content--active');
    } else if (
      filtersDistanceFromTop > 0 &&
      headerContentRef.current?.classList.contains('talent-activity__header-content--active')
    ) {
      headerContentRef.current?.classList.remove('talent-activity__header-content--active');
    }

    if (!isLoading && pagination?.hasMoreResults && distanceFromBottom < 100) {
      fetchMore({
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (fetchMoreResult) {
            const { result: newResult, pagination: newPagination } = fetchMoreResult.talentActivityFeed;

            if (result?.length) {
              recordSignals({
                currentLimit: pagination?.limit || 0,
                items: result.map(item => ({ id: item.talent.user.id, type: SignalEntityTypeEnum.USER })),
                signalType: SignalTypeEnum.VIEW_TALENT_ACTIVITY_FEED_USER,
              });
            }

            return {
              talentActivityFeed: {
                ...prevResult.talentActivityFeed,
                pagination: {
                  ...prevResult.talentActivityFeed.pagination,
                  hasMoreResults: newPagination.hasMoreResults,
                  maxCursor: newPagination.maxCursor,
                },
                result: [...prevResult.talentActivityFeed.result, ...newResult],
              },
            };
          }

          return prevResult;
        },
        variables: {
          filterInput: {
            includeNonVerifiedUsers: shouldIncludeNonVerifiedUsers,
            openingIds: selectedPositionIds,
            signals: selectedSignal,
          },
          paginationOptions: {
            limit: 10,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }
  }, 300);

  useLayoutEffect(() => {
    window.addEventListener('scroll', loadMore);

    return () => {
      window.removeEventListener('scroll', loadMore);

      loadMore.cancel();
    };
  }, [isLoading, pagination]);

  const handleOpeningPositionClick = (openingId: string) => {
    history.push(createLink({ link: FUNNEL_ROUTE, params: { openingId } }));
  };

  return (
    <div className="talent-activity">
      <div className="talent-activity__header">
        <div className="talent-activity__header-container">
          <h3 className="talent-activity__header-title">Activity</h3>

          <div className="talent-activity__header-content" ref={headerContentRef}>
            <Checkbox
              label={'Include anonymous views'}
              onChange={value => setShouldIncludeNonVerifiedUsers(value)}
              value={shouldIncludeNonVerifiedUsers}
              wrapperClassName="talent-activity__non-verified-checkbox"
            />
          </div>
        </div>
      </div>

      <Wrapper>
        <div className="talent-activity__content">
          <Banner />

          <div className="talent-activity__body">
            <div className="talent-activity__filters-wrapper">
              <Filters location={location} />
            </div>

            <div className="talent-activity__data">
              {!(!isLoading && !result?.length) && (
                <>
                  <Statistics
                    applicantsCount={applicantsCount}
                    isLoading={isStatisticsLoading}
                    offerViews={offerViews}
                  />
                  <div className="talent-activity__filter fade-in" ref={checkBoxWrapperRef}>
                    <Checkbox
                      label={'Include anonymous views'}
                      onChange={value => setShouldIncludeNonVerifiedUsers(value)}
                      value={shouldIncludeNonVerifiedUsers}
                      wrapperClassName="talent-activity__non-verified-checkbox"
                    />
                  </div>
                </>
              )}
              <TalentsFeed
                isLoading={isLoading}
                onMessaged={updateRecruiterState}
                onOpeningPositionClick={(openingId: string) => handleOpeningPositionClick(openingId)}
                talentFeed={result || []}
                urlLocation={location}
              />
            </div>
          </div>
        </div>
      </Wrapper>
    </div>
  );
};
